import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { SideCart } from '../components/SideCart';
import { ChatBot } from '../components/ChatBot';
import { useCart } from '../context/CartContext';
import { products } from '../data/products';
import { useShopifyPrice } from '../hooks/useShopifyPrice';

const productList = [
  { ...products.rush, accent: '#4A7C59' },
  { ...products.synaptiq, accent: '#6B5B8A' },
  { ...products.vitra, accent: '#B8704D' },
];

function ProductRow({ product, index }) {
  const navigate = useNavigate();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const { price, loading } = useShopifyPrice(product.id);
  const displayPrice = price || product.price;
  const isEven = index % 2 === 0;

  return (
    <div
      ref={ref}
      className="border-b"
      style={{ borderColor: 'rgba(26,42,26,0.08)' }}
    >
      <div className="grid md:grid-cols-2 min-h-[520px]">
        {/* Image panel */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className={`flex items-center justify-center py-16 px-12 ${isEven ? 'md:order-1' : 'md:order-2'}`}
          style={{ background: '#F0EDE6' }}
        >
          <motion.img
            src={product.images ? product.images[0] : product.image}
            alt={product.title}
            className="w-56 md:w-72 object-contain drop-shadow-xl"
            initial={{ y: 20, opacity: 0 }}
            animate={inView ? { y: 0, opacity: 1 } : {}}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -6 }}
          />
        </motion.div>

        {/* Text panel */}
        <motion.div
          initial={{ opacity: 0, x: isEven ? 24 : -24 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className={`flex flex-col justify-center py-16 px-12 ${isEven ? 'md:order-2' : 'md:order-1'}`}
          style={{ background: '#FAF9F6' }}
        >
          <p className="text-xs font-medium tracking-[0.25em] uppercase mb-5" style={{ color: '#B8704D' }}>
            {String(index + 1).padStart(2, '0')} — {product.subtitle}
          </p>

          <h2 className="text-5xl md:text-6xl font-light mb-5" style={{ color: '#1A2A1A' }}>
            {product.title}
          </h2>

          <p className="text-sm leading-relaxed mb-8" style={{ color: 'rgba(26,42,26,0.6)', maxWidth: '44ch' }}>
            {product.description}
          </p>

          {/* Key benefits — 3 inline */}
          <div className="flex flex-wrap gap-x-6 gap-y-2 mb-10">
            {product.benefits.slice(0, 3).map((b, i) => (
              <span key={i} className="text-xs" style={{ color: 'rgba(26,42,26,0.45)' }}>
                · {b}
              </span>
            ))}
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`/products/${product.id}`)}
              className="px-8 py-3 text-sm font-medium tracking-wide transition-colors"
              style={{ background: '#1A2A1A', color: '#FAF9F6' }}
              onMouseEnter={e => e.currentTarget.style.background = '#243424'}
              onMouseLeave={e => e.currentTarget.style.background = '#1A2A1A'}
            >
              {loading ? '...' : `Shop — $${displayPrice.toFixed(2)}`}
            </button>
            <button
              onClick={() => navigate('/bundle')}
              className="px-8 py-3 text-sm font-medium tracking-wide transition-colors border"
              style={{ border: '1px solid rgba(26,42,26,0.15)', color: '#1A2A1A', background: 'transparent' }}
              onMouseEnter={e => e.currentTarget.style.background = '#E8DCC4'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              Bundle & Save
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export function ProductsPage() {
  const { isCartOpen, setIsCartOpen, cartItems, updateQuantity, removeItem, cartItemsCount } = useCart();
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const heroInView = useInView(heroRef, { once: true });

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: '#FAF9F6' }}>
      <Navigation
        onCartOpen={() => setIsCartOpen(true)}
        cartItemsCount={cartItemsCount}
      />

      {/* Hero */}
      <section className="pt-32 pb-16 border-b" style={{ background: '#FAF9F6', borderColor: 'rgba(26,42,26,0.08)' }}>
        <div className="container mx-auto px-6">
          <motion.div
            ref={heroRef}
            initial={{ opacity: 0, y: 24 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="text-xs font-medium tracking-[0.3em] uppercase mb-6" style={{ color: '#B8704D' }}>
              The Range
            </p>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <h1 className="text-5xl md:text-7xl font-light leading-tight" style={{ color: '#1A2A1A' }}>
                Three formulas.<br />
                <span style={{ color: 'rgba(26,42,26,0.28)' }}>One standard.</span>
              </h1>
              <p className="text-sm leading-relaxed max-w-xs" style={{ color: 'rgba(26,42,26,0.5)' }}>
                Each product is built on the same exacting foundation — organic, third-party tested, and standardized to research-backed doses.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Product rows */}
      <section>
        {productList.map((product, i) => (
          <ProductRow key={product.id} product={product} index={i} />
        ))}
      </section>

      {/* Bundle CTA strip */}
      <section className="py-20" style={{ background: '#1A2A1A' }} data-bubble-theme="light">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-xs font-medium tracking-[0.25em] uppercase mb-4" style={{ color: '#B8704D' }}>
              Better Together
            </p>
            <h2 className="text-4xl md:text-5xl font-light mb-4" style={{ color: '#FAF9F6' }}>
              Get all three. Save more.
            </h2>
            <p className="text-sm mb-10" style={{ color: 'rgba(232,220,196,0.45)' }}>
              The complete Berleto stack — bundled for maximum impact.
            </p>
            <button
              onClick={() => navigate('/bundle')}
              className="px-10 py-4 text-sm font-medium tracking-wide transition-colors"
              style={{ background: '#B8704D', color: '#FAF9F6' }}
              onMouseEnter={e => e.currentTarget.style.background = '#a56340'}
              onMouseLeave={e => e.currentTarget.style.background = '#B8704D'}
            >
              View Bundle
            </button>
          </motion.div>
        </div>
      </section>

      <Footer />

      <SideCart
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={cartItems}
        updateQuantity={updateQuantity}
        removeItem={removeItem}
      />

      <ChatBot />
    </div>
  );
}
